import React from 'react';
import clsx from 'clsx';
import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/20/solid';
import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@oysterjs/ui/table';
import { ProLabel } from '~/components/pro-label';
import type {
  VoiceAgentSettingsTransferDirectoryEntryInput,
  VoiceAgentTransferLineOfBusinessInput
} from './voice_agent_form_input';
import {
  EXTERNAL_TRANSFER_CALL_TYPE_OPTIONS,
  INTERNAL_TRANSFER_CALL_TYPE_OPTIONS
} from './voice_transfer_directory_entry_fields';

/** `fullIndex` addresses edits, removals and validation paths; sorting never changes it. */
export type TransferDirectoryRow = {
  entry: VoiceAgentSettingsTransferDirectoryEntryInput;
  fullIndex: number;
};

type SortColumn = 'name' | 'number' | 'extension';
type SortDirection = 'asc' | 'desc';

const CALL_TYPE_LABELS = new Map(
  [...INTERNAL_TRANSFER_CALL_TYPE_OPTIONS, ...EXTERNAL_TRANSFER_CALL_TYPE_OPTIONS].map(
    ([value, label]) => [value, label]
  )
);

/** How many entries a removal confirmation names before counting the rest. */
const MAX_NAMED = 5;

const entryName = (entry: VoiceAgentSettingsTransferDirectoryEntryInput) =>
  entry.name || entry.label || '';

/** Lets "5551234" match "+1 (555) 123-4567"; applied to both query and entry. */
const digits = (value: string) => value.replace(/\D/g, '');

function matchesQuery(
  entry: VoiceAgentSettingsTransferDirectoryEntryInput,
  query: string
): boolean {
  const haystack = [
    entryName(entry),
    entry.number || '',
    entry.extension || '',
    entry.servicingCode || '',
    ...(entry.aliases || []),
    ...(entry.callTypes || []).map((ct) => CALL_TYPE_LABELS.get(ct) || '')
  ]
    .join(' ')
    .toLowerCase();

  if (haystack.includes(query.toLowerCase())) {
    return true;
  }

  const queryDigits = digits(query);
  return queryDigits.length > 0 && digits(entry.number || '').includes(queryDigits);
}

function sortValue(row: TransferDirectoryRow, column: SortColumn): string {
  switch (column) {
    case 'name':
      return entryName(row.entry);
    case 'number':
      return digits(row.entry.number || '');
    case 'extension':
      return row.entry.extension || '';
  }
}

/** Entries beyond this many need the Pro plan. */
export const PRO_ENTRY_LIMIT = 2;

const STICKY_HEADER =
  'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 dark:bg-neutral-800 backdrop-blur-sm';

function SortableHeader({
  column,
  label,
  sort,
  onSort,
  className
}: {
  column: SortColumn;
  label: string;
  sort: { column: SortColumn; direction: SortDirection } | undefined;
  onSort: (column: SortColumn) => void;
  className?: string;
}) {
  const active = sort?.column === column;
  const Icon = !active
    ? ChevronUpDownIcon
    : sort.direction === 'asc'
      ? ChevronUpIcon
      : ChevronDownIcon;

  return (
    <TableHeader className={clsx(className, STICKY_HEADER)}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group -mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-neutral-900 dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
      >
        {label}
        <Icon
          className={clsx(
            'h-4 w-4 shrink-0',
            active
              ? 'text-neutral-700 dark:text-neutral-200'
              : 'text-neutral-400 opacity-0 group-hover:opacity-100 dark:text-neutral-500'
          )}
        />
      </button>
    </TableHeader>
  );
}

function PlainHeader({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <TableHeader className={clsx(className, STICKY_HEADER)}>{children}</TableHeader>;
}

export function TransferDirectoryTable({
  rows,
  entryTypeNoun,
  extensionEditable,
  hasAdvancedCustomization,
  lineOfBusinessCatalog,
  isProOnlyEntry,
  hasEntryError,
  onOpenEntry,
  onRemoveEntries,
  onAddEntry,
  addDisabled,
  addDisabledReason
}: {
  rows: TransferDirectoryRow[];
  /** Used in the empty state and the remove confirmation, e.g. "internal". */
  entryTypeNoun: string;
  extensionEditable: boolean;
  hasAdvancedCustomization: boolean | undefined;
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[];
  isProOnlyEntry: (fullIndex: number) => boolean;
  hasEntryError: (fullIndex: number) => boolean;
  onOpenEntry: (fullIndex: number) => void;
  onRemoveEntries: (fullIndexes: number[]) => void;
  onAddEntry: () => void;
  addDisabled: boolean;
  addDisabledReason?: string;
}) {
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState<{ column: SortColumn; direction: SortDirection }>();
  const [selected, setSelected] = React.useState<Set<number>>(new Set());

  // Selection holds indexes, which shift when an entry is removed elsewhere (slideout or
  // the other table). Follow each entry to its new index and drop removed ones; an entry
  // not found was edited in place, unless the directory shrank.
  const previousRows = React.useRef(rows);
  React.useLayoutEffect(() => {
    const before = previousRows.current;
    previousRows.current = rows;
    const entryAt = new Map(before.map(({ entry, fullIndex }) => [fullIndex, entry]));
    const nowAt = new Map(rows.map(({ entry, fullIndex }) => [entry, fullIndex]));
    const shrank = rows.length < before.length;
    setSelected((current) => {
      const followed = new Set<number>();
      for (const index of current) {
        const entry = entryAt.get(index);
        const moved = entry === undefined ? undefined : nowAt.get(entry);
        if (moved !== undefined) {
          followed.add(moved);
        } else if (!shrank) {
          followed.add(index);
        }
      }
      const same = followed.size === current.size && [...followed].every((i) => current.has(i));
      return same ? current : followed;
    });
  }, [rows]);

  const lineOfBusinessLabels = React.useMemo(
    () => new Map(lineOfBusinessCatalog.map((lob) => [lob.id, lob.label])),
    [lineOfBusinessCatalog]
  );

  const visible = React.useMemo(() => {
    const filtered = query.trim()
      ? rows.filter(({ entry }) => matchesQuery(entry, query.trim()))
      : rows;
    if (!sort) {
      return filtered;
    }
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = sortValue(a, sort.column);
      const right = sortValue(b, sort.column);
      // Blanks sort last in both directions.
      if (!left !== !right) {
        return left ? -1 : 1;
      }
      return factor * left.localeCompare(right, undefined, { numeric: true });
    });
  }, [rows, query, sort]);

  const toggleSort = (column: SortColumn) =>
    setSort((prev) =>
      prev?.column !== column
        ? { column, direction: 'asc' }
        : prev.direction === 'asc'
          ? { column, direction: 'desc' }
          : undefined
    );

  const selectableVisible = visible.filter(({ fullIndex }) => !isProOnlyEntry(fullIndex));
  const selectedVisibleCount = selectableVisible.filter(({ fullIndex }) =>
    selected.has(fullIndex)
  ).length;
  const allVisibleSelected =
    selectableVisible.length > 0 && selectedVisibleCount === selectableVisible.length;

  const toggleAllVisible = (checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const { fullIndex } of selectableVisible) {
        if (checked) {
          next.add(fullIndex);
        } else {
          next.delete(fullIndex);
        }
      }
      return next;
    });

  const toggleOne = (fullIndex: number, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(fullIndex);
      } else {
        next.delete(fullIndex);
      }
      return next;
    });

  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  /** Captured on open so the names don't change while the dialog fades out. */
  const [removingNames, setRemovingNames] = React.useState<string[]>([]);

  const removeSelected = () => {
    setConfirmingRemove(false);
    onRemoveEntries([...selected]);
    // Removal renumbers later entries, so a kept selection would point at the wrong ones.
    setSelected(new Set());
  };

  const errorCount = rows.filter(({ fullIndex }) => hasEntryError(fullIndex)).length;

  const addButton = (
    <Button
      color="sky"
      disabled={addDisabled}
      title={addDisabled ? addDisabledReason : undefined}
      onClick={onAddEntry}
    >
      Add Directory Entry
      {addDisabled && <ProLabel />}
    </Button>
  );

  return (
    <div className="space-y-3">
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[12rem]">
            <InputGroup>
              <MagnifyingGlassIcon data-slot="icon" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                placeholder={`Search ${rows.length} ${entryTypeNoun} ${rows.length === 1 ? 'entry' : 'entries'}`}
                aria-label={`Search ${entryTypeNoun} directory`}
              />
            </InputGroup>
          </div>

          {selected.size > 0 ? (
            <Button
              color="red"
              onClick={() => {
                setRemovingNames(
                  rows
                    .filter(({ fullIndex }) => selected.has(fullIndex))
                    .map(({ entry, fullIndex }) => entryName(entry) || `Entry ${fullIndex + 1}`)
                );
                setConfirmingRemove(true);
              }}
            >
              Remove {selected.size} selected
            </Button>
          ) : (
            addButton
          )}
        </div>
      )}

      {errorCount > 0 && (
        <div className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
          <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
          {errorCount === 1
            ? '1 entry needs attention.'
            : `${errorCount} entries need attention.`}{' '}
          Open it to see what is missing.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-10 text-center">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            No {entryTypeNoun} transfer entries yet.
          </p>
          <div className="mt-3">{addButton}</div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
          {/* CSV imports can add hundreds of entries, so scroll within a fixed height. */}
          <div className="max-h-[28rem] overflow-y-auto">
            {/* bleed without the negative margin, so row highlights reach the border. */}
            <Table bleed stickyHeader dense className="!mx-0 [--gutter:theme(spacing.4)]">
              <TableHead>
                <TableRow>
                  {/* Inset (here and in rows) to clear the rounded edge. */}
                  <PlainHeader className="w-10 !pl-5">
                    <Checkbox
                      color="sky"
                      aria-label={`Select all visible ${entryTypeNoun} entries`}
                      disabled={selectableVisible.length === 0}
                      checked={allVisibleSelected}
                      indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                      onChange={toggleAllVisible}
                    />
                  </PlainHeader>
                  <SortableHeader column="name" label="Name" sort={sort} onSort={toggleSort} />
                  <SortableHeader
                    column="number"
                    label="Phone number"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  {extensionEditable && (
                    <SortableHeader
                      column="extension"
                      label="Ext."
                      sort={sort}
                      onSort={toggleSort}
                      className="w-20"
                    />
                  )}
                  <PlainHeader>Call types</PlainHeader>
                  <PlainHeader>Lines of business</PlainHeader>
                  <PlainHeader className="w-16" />
                </TableRow>
              </TableHead>

              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={extensionEditable ? 7 : 6}>
                      <div className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                        No entries match &ldquo;{query.trim()}&rdquo;.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {visible.map(({ entry, fullIndex }) => {
                  const proOnly = isProOnlyEntry(fullIndex);
                  const invalid = hasEntryError(fullIndex);
                  const callTypes = (entry.callTypes || [])
                    .map((ct) => CALL_TYPE_LABELS.get(ct) || ct)
                    .join(', ');
                  const linesOfBusiness = (entry.lineOfBusinessIds || [])
                    .map((id) => lineOfBusinessLabels.get(id) || id)
                    .join(', ');

                  return (
                    <TableRow
                      key={fullIndex}
                      onClick={() => onOpenEntry(fullIndex)}
                      className={clsx(
                        'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/60',
                        selected.has(fullIndex) && 'bg-sky-50 dark:bg-sky-500/10'
                      )}
                    >
                      <TableCell className="!pl-5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          color="sky"
                          aria-label={`Select ${entryName(entry) || `entry ${fullIndex + 1}`}`}
                          disabled={proOnly}
                          checked={selected.has(fullIndex)}
                          onChange={(checked) => toggleOne(fullIndex, checked)}
                        />
                      </TableCell>

                      <TableCell>
                        <div className="flex min-w-0 max-w-[16rem] items-center gap-1.5">
                          {invalid && (
                            <ExclamationCircleIcon
                              className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400"
                              aria-label="This entry has a validation error"
                            />
                          )}
                          <span
                            className={clsx(
                              'truncate font-medium',
                              entryName(entry)
                                ? 'text-neutral-900 dark:text-white'
                                : 'italic text-neutral-400 dark:text-neutral-500'
                            )}
                          >
                            {entryName(entry) || 'Untitled entry'}
                          </span>
                          {proOnly && <ProLabel />}
                        </div>
                      </TableCell>

                      <TableCell className="tabular-nums">
                        {entry.number || (
                          <span className="italic text-neutral-400 dark:text-neutral-500">
                            No phone number
                          </span>
                        )}
                      </TableCell>

                      {extensionEditable && (
                        <TableCell className="tabular-nums text-neutral-500 dark:text-neutral-400">
                          {entry.extension || '—'}
                        </TableCell>
                      )}

                      <TableCell>
                        <div
                          className="max-w-[14rem] truncate text-neutral-500 dark:text-neutral-400"
                          title={callTypes}
                        >
                          {callTypes || 'All'}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div
                          className="max-w-[14rem] truncate text-neutral-500 dark:text-neutral-400"
                          title={linesOfBusiness}
                        >
                          {linesOfBusiness || 'All'}
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <span className="text-sm text-sky-600 dark:text-sky-400">Edit</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          {visible.length === rows.length
            ? `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`
            : `${visible.length} of ${rows.length} entries`}
          {!hasAdvancedCustomization && ` · Pro is required beyond ${PRO_ENTRY_LIMIT} entries`}
        </div>
      )}

      <Dialog open={confirmingRemove} onClose={() => setConfirmingRemove(false)} size="md">
        <DialogTitle>
          Remove {removingNames.length} {entryTypeNoun}{' '}
          {removingNames.length === 1 ? 'entry' : 'entries'}?
        </DialogTitle>
        <DialogDescription>
          {removingNames.slice(0, MAX_NAMED).join(', ')}
          {removingNames.length > MAX_NAMED && ` and ${removingNames.length - MAX_NAMED} more`}
        </DialogDescription>
        <DialogBody>
          <p className="text-sm text-neutral-700 dark:text-neutral-200">
            Cara will stop transferring calls to {removingNames.length === 1 ? 'it' : 'them'} once
            you save this page.
          </p>
        </DialogBody>
        <DialogActions>
          <Button color="white" onClick={() => setConfirmingRemove(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={removeSelected}>
            Remove {removingNames.length} {removingNames.length === 1 ? 'entry' : 'entries'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
